import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import useOutsideClick from '../hooks/useOutsideClick'

function CustomSelect({
  options = [],
  onChange = null,
  selectedOption = null,
}) {
  // Internal state for uncontrolled mode
  const [internalSelectedOption, setInternalSelectedOption] = useState(
    selectedOption || options[0] || { label: 'All', value: null }
  )

  // Synchronize internal state with controlled `selectedOption`
  useEffect(() => {
    if (selectedOption) {
      setInternalSelectedOption(selectedOption)
    }
  }, [selectedOption])

  // Determine the currently selected option (controlled or internal)
  const selected = selectedOption || internalSelectedOption

  // Handle dropdown visibility and outside click
  const { dropdownRef, isDropdownOpen, setIsDropdownOpen } = useOutsideClick()

  // Toggle dropdown visibility
  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen)

  // Handle option selection
  const handleOptionClick = (option) => {
    if (!selectedOption) {
      setInternalSelectedOption(option) // Update internal state only if uncontrolled
    }
    setIsDropdownOpen(false)
    if (onChange) {
      onChange(option)
    }
  }

  // Truncate string if it's too long
  function trimString(str = '') {
    return str.length > 19 ? str.slice(0, 18) + '...' : str
  }

  return (
    <div className='custom-select' ref={dropdownRef}>
      <div
        className='selected-option'
        onClick={toggleDropdown}
        role='button'
        aria-haspopup='listbox'
        aria-expanded={isDropdownOpen}
      >
        {selected.imgSrc && (
          <img
            src={selected.imgSrc}
            alt={selected.label || 'Option'}
            onError={(e) => (e.target.style.display = 'none')}
          />
        )}
        <span>{selected ? trimString(selected.label) : 'All'}</span>
        <i className='bi bi-caret-down-fill' style={{ marginLeft: 'auto' }} />
      </div>
      {isDropdownOpen && (
        <div className='options' role='listbox'>
          {options.map((option) => (
            <div
              key={option.value}
              className='option'
              role='option'
              onClick={() => handleOptionClick(option)}
            >
              {option.imgSrc && (
                <img
                  src={option.imgSrc}
                  alt={option.label || 'Option'}
                  onError={(e) => (e.target.style.display = 'none')}
                />
              )}
              <span>{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

CustomSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any,
      imgSrc: PropTypes.string,
    })
  ).isRequired,
  onChange: PropTypes.func,
  selectedOption: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    imgSrc: PropTypes.string,
  }),
}

export default CustomSelect
